import { useRef, useState } from 'react';

import {
  Box,
  Select as MantineSelect,
  SelectProps as MantineSelectProps,
  rem,
} from '@mantine/core';
import { useElementSize, useUncontrolled } from '@mantine/hooks';
import { IconChevronDown } from 'ui-icons';

import { Text } from '../../Typography';
import {
  Countries,
  Months,
  States,
  Time,
  TimeZones,
  Years,
  paginationOptions,
} from '../../Utils/utils';
import classes from './Select.module.css';

const SelectDefaults = MantineSelect.extend({
  classNames: {
    root: classes.root,
    input: classes.input,
    dropdown: classes.dropdown,
    option: classes.option,
    options: classes.options,
  },
  defaultProps: {
    size: 'md',
    rightSection: <IconChevronDown size="12px" />,
    rightSectionWidth: rem(28),
    withCheckIcon: false,
  },
});

interface SelectProps extends MantineSelectProps {
  /**
   * If true, the left section will auto-size to the width of the content
   *
   * @default true
   */
  withAutoSizeLeftSection?: boolean;
}

const Select = ({ withAutoSizeLeftSection = true, wrapperProps = {}, ...props }: SelectProps) => {
  const { ref, width } = useElementSize();

  if (withAutoSizeLeftSection) {
    return (
      <MantineSelect
        {...props}
        leftSectionWidth={width + 16}
        leftSection={<Box ref={ref}>{props.leftSection}</Box>}
        wrapperProps={{ ...wrapperProps, readOnly: props.readOnly }}
        scrollAreaProps={{
          type: 'auto',
        }}
      />
    );
  }
  return <MantineSelect {...props} />;
};

const AutofillSelect = (props: SelectProps) => {
  const [autoFillKey, setAutoFillKey] = useState(1);
  const isSelectOpenRef = useRef<boolean>(false);
  const [_value, handleChange] = useUncontrolled({
    value: props?.value || undefined,
    onChange: props?.onChange,
  });

  return (
    <Select
      {...props}
      key={autoFillKey}
      value={_value}
      searchable
      filterDataOnExactSearchMatch
      // filter={(value, item) =>
      //   (item.label && item?.label.toLowerCase().includes(value.toLowerCase().trim())) ||
      //   item.value.toLowerCase().includes(value.toLowerCase().trim())
      // }
      onSearchChange={(query) => {
        if (isSelectOpenRef.current === false) {
          if (query && !_value) {
            handleChange(query);
            setAutoFillKey((prev) => prev + 1);
          }
        }
      }}
      // @ts-expect-error -- Remove after update
      onChange={handleChange}
      onDropdownOpen={() => {
        isSelectOpenRef.current = true;
      }}
      onDropdownClose={() => {
        isSelectOpenRef.current = false;
      }}
      hoverOnSearchChange
      selectOnBlur
    />
  );
};

interface AutoFillSelectProps extends Omit<SelectProps, 'data'> {
  autoFillValue: 'address-level1' | 'cc-exp-month' | 'cc-exp-year' | 'country';
}

const _States = (props: Partial<AutoFillSelectProps>) => {
  return (
    <AutofillSelect data={States} placeholder="State" autoComplete="address-level1" {...props} />
  );
};
const _Country = (props: Partial<AutoFillSelectProps>) => {
  return (
    <AutofillSelect
      data={Countries}
      placeholder="Country"
      autoComplete="address-level1"
      {...props}
    />
  );
};

const _Months = (props: Partial<AutoFillSelectProps>) => {
  return (
    <AutofillSelect data={Months} placeholder="Month" autoComplete="cc-exp-month" {...props} />
  );
};
const _Years = (props: Partial<AutoFillSelectProps>) => {
  return <AutofillSelect data={Years} placeholder="Year" autoComplete="cc-exp-year" {...props} />;
};

const _Times = (props: Omit<SelectProps, 'data'>) => {
  return <Select data={Time} defaultValue={'12:00:00'} {...props} />;
};
const _TimeZone = (props: Omit<SelectProps, 'data'>) => {
  const currentTimeZone = new Date().getTimezoneOffset() / 60;

  return <Select data={TimeZones} defaultValue={String(currentTimeZone)} {...props} />;
};

const _Pagination = (props: Partial<SelectProps>) => {
  return <Select data={paginationOptions} {...props} leftSection={<Text fz="sm">Display</Text>} />;
};

Select.Months = _Months;
Select.Years = _Years;
Select.States = _States;
Select.Countries = _Country;
Select.Times = _Times;
Select.Pagination = _Pagination;
Select.TimeZone = _TimeZone;
export type { ComboboxItem, OptionsFilter, SelectProps } from '@mantine/core';
export { Select, SelectDefaults };
